import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { formatAmountWithCurrencyCode } from '@/helpers/finance';

import { InvoiceDetails } from '../../types';

interface InvoiceTotalsCardProps {
  details: InvoiceDetails;
  isReadOnly: boolean;
  currencyCode: string;
  totalAmount: string;
  balance: string;
  totalErrorMessage?: string;
}

/** Displays invoice monetary values and the aggregate line-items validation error. */
export function InvoiceTotalsCard({
  details,
  isReadOnly,
  currencyCode,
  totalAmount,
  balance,
  totalErrorMessage,
}: InvoiceTotalsCardProps) {
  const { t } = useTranslation('authenticated.invoices');
  const displayedCurrencyCode = isReadOnly ? details.currencyCode : currencyCode;

  return (
    <Card className="bg-surface">
      <CardHeader>
        <CardTitle>{t('details.totals')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <InvoiceTotalRow
          label={t('details.fields.invoiceTotal')}
          value={formatAmountWithCurrencyCode(
            isReadOnly ? details.totalAmount : totalAmount,
            displayedCurrencyCode
          )}
        />
        <InvoiceTotalRow
          label={t('details.fields.paid')}
          value={formatAmountWithCurrencyCode(details.amountPaid, displayedCurrencyCode)}
        />
        <Separator />
        <InvoiceTotalRow
          label={t('details.fields.balance')}
          value={formatAmountWithCurrencyCode(
            isReadOnly ? details.balance : balance,
            displayedCurrencyCode
          )}
          isEmphasized
        />
        {totalErrorMessage && (
          <div className="flex flex-col gap-1 pt-4 text-sm font-medium text-destructive">
            <p>{t(totalErrorMessage)}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InvoiceTotalRow({
  label,
  value,
  isEmphasized = false,
}: {
  label: string;
  value: string;
  isEmphasized?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={isEmphasized ? 'font-semibold tabular-nums' : 'font-medium tabular-nums'}>
        {value}
      </span>
    </div>
  );
}
